import React, { useRef, useState, useEffect } from 'react'
import { GatsbyImage } from 'gatsby-plugin-image'
import PropTypes from 'prop-types'
import { v4 as uuidv4 } from 'uuid'
import initTealiumTracker from 'tealium-tracker'
import { teliumDefaults } from '../../tracking/telium'

// Components
import AnimateSlideText from '../animation/AnimateSlideText'
import AnimateScaleIn from '../animation/AnimateScaleIn'
import Button from '../Button'
import ContainedWrap from '../ContainedWrap'
import Container from '../Container'
import FormInput from '../Forms/input'
import FormSelect from '../Forms/select'
import Grid from '../Grid'
import GridItem from '../GridItem'
import ImageLabel from '../ImageLabel'
import RichText from '../RichText'
import ScrollSection from '../ScrollSection'
import Spacer from '../Spacer'

// Data
import { countryList } from './data/country-list'

// Styles
import {
  NewsletterWrapper,
  NewsletterInner,
  NewsletterText,
  NewsletterForm,
  NewsletterFormInner,
  NewsletterInputWrap,
  NewsletterImageWrap,
  NewsletterSubmitMessage,
} from './index.style'
import { TextBody } from '../../styles/vars/textStyles.style'

const NewsletterSection = ({
  id,
  heading,
  text,
  image,
  imageLabel,
  color,
  linkId,
  data,
}) => {
  const submissionURL = '/bin/services/royal/forms/emailSignup'

  /**
   * Ref for each input field
   * Not currently used but may be necessary when the form is hooked up
   */
  const firstNameRef = useRef(null),
    lastNameRef = useRef(null),
    emailRef = useRef(null),
    locationRef = useRef(null),
    formRef = useRef(null)

  const [feedbackMessage, setFeedbackMessage] = useState(null),
    [formHidden, setFormHidden] = useState(false),
    [formFields, setFormFields] = useState({
      firstName: '',
      lastName: '',
      email: '',
      country: '',
      guid: '',
      pageSource: 'ultimateworldcruise',
      lastProduct: null,
      selectedSignUpCountry: '',
    })

  // Generate a unique ID value
  useEffect(() => {
    const idValue = uuidv4()

    setFormFields(f => ({
      ...f,
      guid: idValue,
    }))
  }, [])

  const handleSubmit = async e => {
    e.preventDefault()

    const response = await fetch(submissionURL, {
      method: 'post',
      body: new URLSearchParams({
        ...formFields,
      }),
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    })

    const data = await response.json()

    const { trackEvent } = initTealiumTracker()

    trackEvent({
      pageName: 'ultimatecruise:signup',
      ...teliumDefaults,
    })

    if (data?.type === 'SUCCESS') {
      setFeedbackMessage('Thanks for signing up!')
      setFormHidden(true)
    } else {
      setFeedbackMessage(
        'Sorry, there was an error. Please refresh the page and try again.'
      )
      setFormHidden(true)
    }
  }

  return (
    <ScrollSection>
      <NewsletterWrapper id={linkId && linkId}>
        <NewsletterInner background={color}>
          <NewsletterText>
            <Spacer size={[100, 117]} />
            <Container>
              <Grid>
                <GridItem
                  tabletP={8}
                  tabletPStart={2}
                  tabletL={5}
                  tabletLStart={1}
                  tabletLAlign={'end'}
                  desk={4}
                  deskStart={2}
                  deskAlign={'end'}
                  deskL={3}
                  deskLStart={2}
                  deskLAlign={'end'}
                >
                  <ContainedWrap size={438} center={false}>
                    <RichText
                      id={`${id}-heading`}
                      content={heading}
                      forceHeading
                      delayEm={true}
                    />
                    <Spacer size={15} />
                  </ContainedWrap>
                </GridItem>
                <GridItem
                  tabletP={8}
                  tabletPStart={3}
                  tabletL={4}
                  tabletLStart={9}
                  tabletLHeight={2}
                  tabletLAlign={`center`}
                >
                  <NewsletterImageWrap>
                    <AnimateScaleIn id={`${id}-image`} position={'relative'}>
                      <GatsbyImage
                        image={image.gatsbyImageData}
                        alt={imageLabel}
                      />
                    </AnimateScaleIn>
                    {imageLabel && (
                      <ImageLabel id={`${id}-label`} imageLabel={imageLabel} />
                    )}
                  </NewsletterImageWrap>
                </GridItem>
                <GridItem
                  tabletP={8}
                  tabletPStart={2}
                  tabletL={8}
                  tabletLStart={1}
                  desk={6}
                  deskStart={2}
                >
                  <NewsletterSubmitMessage formHidden={formHidden}>
                    <TextBody>{feedbackMessage}</TextBody>
                  </NewsletterSubmitMessage>
                  <NewsletterForm
                    name={`newsletter-sign-up`}
                    onSubmit={event => handleSubmit(event)}
                    ref={formRef}
                    encType="multipart/form-data"
                    method="post"
                    formHidden={formHidden}
                  >
                    <NewsletterFormInner>
                      <NewsletterInputWrap>
                        <FormInput
                          name={data.formFirstNameLabel}
                          id={`firstName`}
                          type={`text`}
                          forwardRef={firstNameRef}
                          required
                          formFields={formFields}
                          setFormFields={setFormFields}
                        />
                      </NewsletterInputWrap>
                      <NewsletterInputWrap>
                        <FormInput
                          name={data.formLastNameLabel}
                          id={`lastName`}
                          type={`text`}
                          forwardRef={lastNameRef}
                          required
                          formFields={formFields}
                          setFormFields={setFormFields}
                        />
                      </NewsletterInputWrap>
                      <NewsletterInputWrap>
                        <FormInput
                          name={data.formEmailAddressLabel}
                          id={`email`}
                          type={`text`}
                          forwardRef={emailRef}
                          required
                          formFields={formFields}
                          setFormFields={setFormFields}
                        />
                      </NewsletterInputWrap>
                      <NewsletterInputWrap>
                        <FormSelect
                          name={data.formCountrylocationLabel}
                          id={`country`}
                          forwardRef={locationRef}
                          required
                          formFields={formFields}
                          setFormFields={setFormFields}
                          options={countryList}
                        />
                      </NewsletterInputWrap>
                    </NewsletterFormInner>
                    <Spacer size={[30, 40]} />
                    <RichText id={`${id}-text`} content={text} />
                    <Spacer size={[30, 40]} />
                    <AnimateSlideText id={`${id}-cta`}>
                      <Button type="submit" variant="outline">
                        {data.formSignUpLabel}
                      </Button>
                    </AnimateSlideText>
                  </NewsletterForm>
                </GridItem>
              </Grid>
            </Container>
            <Spacer size={[100, 152]} />
          </NewsletterText>
        </NewsletterInner>
      </NewsletterWrapper>
    </ScrollSection>
  )
}

NewsletterSection.propTypes = {
  id: PropTypes.string.isRequired,
  color: PropTypes.string,
  imageLabel: PropTypes.string,
  heading: PropTypes.object,
  text: PropTypes.object,
  image: PropTypes.object,
}

export default NewsletterSection
